/**
 * GroupedListView — iter-195 unified list scaffold.
 *
 * Replaces the duplicated layout shared by Movimientos / Cobros /
 * Pagos: search input + date-range filter + tag-pill filter +
 * group-by selector + grouped rendering with sticky headers +
 * infinite-scroll sentinel + empty state.
 *
 * The row rendering itself stays page-specific via the ``renderRow``
 * prop so each page keeps full control over its actions, swipe gestures
 * and visual hierarchy.
 *
 * Stat chips at the top (optional) provide a single-glance summary —
 * any chip with ``filterId`` set becomes a clickable filter toggle
 * (e.g. "Atrasados", "Próximos 7 días") that the parent reads via
 * ``activeChip`` + ``onChipClick``.
 */
import { useMemo, useState } from "react";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { Search, ChevronDown, X } from "lucide-react";
import { useGroupedByDate } from "../hooks/useGroupedByDate";
import { tx } from "../i18n";

const TONE_CLASSES = {
  slate: "bg-slate-50 border-slate-200 text-slate-600",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
  sky: "bg-sky-50 border-sky-200 text-sky-700",
  violet: "bg-violet-50 border-violet-200 text-violet-700",
};
const TONE_ACTIVE = {
  slate: "bg-slate-700 border-slate-800 text-white",
  amber: "bg-amber-600 border-amber-700 text-white",
  emerald: "bg-emerald-600 border-emerald-700 text-white",
  rose: "bg-rose-600 border-rose-700 text-white",
  sky: "bg-sky-600 border-sky-700 text-white",
  violet: "bg-violet-600 border-violet-700 text-white",
};

export function GroupedListView({
  // Core data
  items,
  isLoading = false,
  groupBy,
  onGroupByChange,
  dateAccessor,
  renderRow,

  // Filters (all controlled by the parent so parent decides
  // whether to fire server queries or filter client-side).
  search = "",
  onSearchChange,
  searchPlaceholder,
  dateFrom = "",
  dateTo = "",
  onDateFromChange,
  onDateToChange,
  tagSuggestions = [],
  tagFilter = "",
  onTagFilterChange,

  // Stat chips at top — array of { id, label, value, tone, filterId? }
  statChips = [],
  activeChip = null,
  onChipClick = null,

  // Optional render slots
  headerRight = null,
  emptyState = null,
  groupSummary = null, // (groupItems) => ReactNode rendered on the right of the group header
  restoredBanner = null, // iter-213.9 — JSX shown above the filter card when a persisted filter set is rehydrated

  // Collapsible groups (iter-196). When true, each group can be
  // collapsed by clicking its header. Defaults open.
  collapsible = true,
  // iter-196e — opt-in sticky behaviour for the filter card. Off by
  // default because parent layouts vary (some have a fixed top nav).
  stickyFilters = false,

  // Infinite scroll
  sentinelRef = null,
  hasMore = false,
  isFetchingMore = false,

  // Test-id namespace so the page keeps its own ids stable.
  testid = "list",
  lang = "es",
  // iter-211.2 — Sort direction within and across groups. "desc"
  // (default) preserves the legacy /history newest-first behaviour;
  // "asc" is what /receivables + /pagos use so the upcoming dates
  // surface at the top.
  order = "desc",
  // iter-211.2 — Optional slot for additional filter controls (e.g.
  // an "Account" select). Rendered inline with the existing filter
  // panel so the UX stays consistent across /history /receivables /pagos.
  extraFilters = null,
}) {
  const isEs = lang === "es";
  const groups = useGroupedByDate(items, groupBy, dateAccessor, lang, order);
  const hasItems = items.length > 0;

  // Stable list of group-by options. iter-213.7 — added "tag" so any
  // page that supplies items with a `tags` array (movements, receivables,
  // payments) can pivot on the user's category libre.
  const groupByOptions = useMemo(() => ([
    { value: "day", label: tx(lang, "Day", "Día", "Dia") },
    { value: "week", label: tx(lang, "Week", "Semana", "Semana") },
    { value: "month", label: tx(lang, "Month", "Mes", "Mês") },
    { value: "tag", label: tx(lang, "Tag", "Etiqueta", "Etiqueta") },
  ]), [lang]);
  // iter-213.7 — Render the trigger label as "Agrupado por X" so the
  // user understands what the dropdown is doing (no extra label needed
  // outside the trigger). Falls back to the raw label if no match.
  const groupedByPrefix = tx(lang, "Grouped by", "Agrupado por", "Agrupado por");
  const currentGroupLabel = groupByOptions.find((o) => o.value === groupBy)?.label || groupBy;

  return (
    <div className="w-full" data-testid={testid}>
      {/* iter-213.9 — "Filtros restaurados" banner. Pages pass their
          own `restoredBanner` JSX (typically rendering when
          `filters.restored && filters.hasActive`). Shown above the
          stat chips so users notice it. */}
      {restoredBanner}

      {/* ── Stat chips row ─────────────────────────────────────── */}
      {statChips.length > 0 && (
        <div
          className={`grid gap-2 mb-3 ${
            statChips.length >= 4 ? "grid-cols-2 sm:grid-cols-4"
              : statChips.length === 3 ? "grid-cols-3"
                : "grid-cols-2"
          }`}
          data-testid={`${testid}-stats`}
        >
          {statChips.map((chip) => {
            const tone = chip.tone || "slate";
            const isActive = activeChip === chip.filterId && chip.filterId != null;
            const isClickable = chip.filterId != null && onChipClick;
            const className = `p-2.5 rounded-lg border text-left transition-all ${
              isActive
                ? `${TONE_ACTIVE[tone] || TONE_ACTIVE.slate} shadow-sm`
                : `${TONE_CLASSES[tone] || TONE_CLASSES.slate} ${isClickable ? "hover:shadow-sm hover:scale-[1.02] cursor-pointer" : ""}`
            }`;
            const inner = (
              <>
                <div className={`text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 ${isActive ? "text-white/90" : ""}`}>
                  {chip.icon ? <chip.icon className="h-3 w-3" /> : null}
                  {chip.label}
                </div>
                <div className="text-base sm:text-lg font-bold tabular-nums truncate">{chip.value}</div>
              </>
            );
            return isClickable ? (
              <button /* design-system: ALLOW legacy-todo */
                key={chip.id}
                type="button"
                className={className}
                onClick={() => onChipClick(isActive ? null : chip.filterId)}
                data-testid={`${testid}-chip-${chip.id}`}
              >{inner}</button>
            ) : (
              <div
                key={chip.id}
                className={className}
                data-testid={`${testid}-chip-${chip.id}`}
              >{inner}</div>
            );
          })}
        </div>
      )}

      {/* ── Filters panel (single horizontal row) ────────────────
          iter-196e — bordered card mirroring the Movements page.
          Sticky behaviour was removed: it overlapped the page header
          on desktop and squeezed the search bar on mobile. Re-enable
          per-page via the ``stickyFilters`` prop when the parent
          knows the correct offset. */}
      {(hasItems || search || dateFrom || dateTo || tagFilter) && (
        <div
          className={`rounded-2xl border border-slate-200 bg-white p-3 mb-3 flex flex-wrap items-center gap-2 ${stickyFilters ? "sticky top-2 z-10 shadow-sm backdrop-blur-md bg-white/95" : ""}`}
          data-testid={`${testid}-filters-panel`}
        >
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              type="text"
              value={search}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder={searchPlaceholder || tx(lang, "Search…", "Buscar…", "Buscar…")}
              className="h-9 pl-8 text-sm"
              data-testid={`${testid}-search`}
            />
          </div>
          {onDateFromChange && (
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => onDateFromChange?.(e.target.value)}
              className="h-9 text-xs w-[140px]"
              placeholder={tx(lang, "From", "Desde", "De")}
              title={tx(lang, "From date", "Desde", "Data inicial")}
              data-testid={`${testid}-date-from`}
            />
          )}
          {onDateToChange && (
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => onDateToChange?.(e.target.value)}
              className="h-9 text-xs w-[140px]"
              title={tx(lang, "To date", "Hasta", "Data final")}
              data-testid={`${testid}-date-to`}
            />
          )}
          {hasItems && onGroupByChange && (
            <Select value={groupBy} onValueChange={onGroupByChange}>
              <SelectTrigger className="h-9 w-[170px] text-xs" data-testid={`${testid}-group-select`}>
                <SelectValue>{`${groupedByPrefix} ${currentGroupLabel.toLowerCase()}`}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {groupByOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {extraFilters}
          {(search || dateFrom || dateTo || tagFilter) && (
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              onClick={() => {
                onSearchChange?.("");
                onDateFromChange?.("");
                onDateToChange?.("");
                onTagFilterChange?.("");
              }}
              className="h-9 px-2 inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700 font-semibold"
              data-testid={`${testid}-filters-clear`}
            >
              <X className="h-3 w-3" />
              {tx(lang, "Clear", "Limpiar", "Limpar")}
            </button>
          )}
          {headerRight}
        </div>
      )}

      {/* ── Tag chips row (outside the filter card, like History) ─ */}
      {tagSuggestions.length > 0 && onTagFilterChange && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid={`${testid}-tag-filter`}>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1">
            {tx(lang, "Tags", "Etiquetas", "Tags")}
          </span>
          {tagSuggestions.slice(0, 8).map((tg) => {
            const active = tagFilter === tg;
            return (
              <button /* design-system: ALLOW legacy-todo */
                key={tg}
                type="button"
                onClick={() => onTagFilterChange(active ? "" : tg)}
                data-testid={`${testid}-tag-${tg}`}
                className={`text-[11px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 ring-1 transition-colors ${
                  active
                    ? "bg-emerald-600 text-white ring-emerald-700"
                    : "bg-white text-slate-700 ring-slate-200 hover:bg-emerald-50 hover:text-emerald-700 hover:ring-emerald-300"
                }`}
              >#{tg}</button>
            );
          })}
        </div>
      )}

      {/* ── List body ──────────────────────────────────────────── */}
      <div className="space-y-4" data-testid={`${testid}-body`}>
        {!isLoading && !hasItems && (
          emptyState != null
            ? emptyState
            : (
              <Card className="p-8 text-center text-slate-500 rounded-2xl border-dashed">
                {isEs ? "Sin resultados." : "No results."}
              </Card>
            )
        )}
        {groups.map(({ key, label, items: groupItems }) => (
          <GroupBlock
            key={key}
            groupKey={key}
            label={label}
            items={groupItems}
            renderRow={renderRow}
            collapsible={collapsible}
            summary={groupSummary ? groupSummary(groupItems) : null}
            testid={`${testid}-group-${key}`}
            isEs={isEs}
          />
        ))}
        {hasMore && (
          <div
            ref={sentinelRef}
            className="text-center text-xs text-slate-400 py-2"
            data-testid={`${testid}-sentinel`}
          >
            {isFetchingMore
              ? (isEs ? "Cargando más…" : "Loading more…")
              : (isEs ? "Desliza para ver más" : "Scroll to see more")}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── GroupBlock — collapsible day/week/month grouping ────────────────
 * iter-196 — mimics the Movements page visual: clean inline header
 * (capitalized period label on the left, aggregated value on the
 * right) with no surrounding chip. Clicking the row toggles collapse.
 */
function GroupBlock({ groupKey, label, items, renderRow, collapsible, summary, testid, isEs }) {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = () => collapsible && setCollapsed((v) => !v);
  void groupKey; // testid already encodes it
  return (
    <div className="space-y-2" data-testid={testid}>
      <div
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : -1}
        onClick={toggle}
        onKeyDown={(e) => { if (collapsible && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); toggle(); } }}
        className={`flex items-center justify-between gap-3 px-1 pt-2 pb-1.5 border-b border-slate-200 select-none ${collapsible ? "cursor-pointer hover:bg-slate-50/60 rounded-md transition-colors" : ""}`}
        aria-expanded={collapsible ? !collapsed : undefined}
        title={collapsible ? (isEs ? (collapsed ? "Expandir" : "Colapsar") : (collapsed ? "Expand" : "Collapse")) : undefined}
        data-testid={`${testid}-header`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {collapsible && (
            <ChevronDown
              className={`h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform ${collapsed ? "-rotate-90" : ""}`}
              data-testid={`${testid}-chevron`}
            />
          )}
          <span className="text-sm font-semibold text-slate-700 capitalize truncate">{label}</span>
          <span className="text-[11px] text-slate-400 tabular-nums shrink-0">· {items.length}</span>
        </div>
        {summary != null && (
          <div className="flex items-center shrink-0" data-testid={`${testid}-summary`}>
            {summary}
          </div>
        )}
      </div>
      {!collapsed && (
        <div className="space-y-2" data-testid={`${testid}-items`}>
          {items.map((item) => renderRow(item))}
        </div>
      )}
    </div>
  );
}
